/**
 * Auth API
 * Contains all API functions related to authentication and user management
 */

import { apiGet, apiPost } from './apiClient';

const reportUserURL = '/report/user';

export interface LoginParams {
    username: string;
    password: string;
}

export interface RegisterParams {
    code: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    username: string;
    firebaseUid?: string;
}

export interface ResetPasswordParams {
    username: string;
    password: string;
    /** Proof of the Firebase reset — what idearify-api's updatePassword verifies. */
    firebaseIdToken?: string;
    code?: string;
}

export interface UpdateUserParams {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
    bio?: string;
    avatar?: string;
    banner?: string;
    subscribed?: string[];
}

export interface UpdatePasswordParams {
    currentPassword: string;
    newPassword: string;
}

export interface DeactivateParams {
    reason: string;
    feedback?: string;
    // Account identity fields the backend needs to locate and delete the
    // right record (id/username/firebaseUid) — kept as an index signature
    // rather than enumerated since callers pass the full stored user object.
    [key: string]: unknown;
}

export interface ReportUserParams {
    id: string;
    idea: string;
    reportedBy: string;
    dateReported: string;
}

export interface SendRecoveryCodeParams {
    email: string;
    username?: string;
}

/**
 * Log in a user
 * @param username The username
 * @param password The password
 * @returns Promise with login result
 */
export const login = async (params: LoginParams) => {
    return apiGet(
        `/login?username=${encodeURIComponent(params.username)}&password=${encodeURIComponent(params.password)}`,
        {},
        '',
    );
};

/**
 * Log out the current user
 * @returns Promise with logout result
 */
export const logout = async () => {
    return apiPost('/logout', {}, {}, 'authenticated', true);
};

/**
 * Register a new user
 * @param params User registration data
 * @returns Promise with registration result
 */
export const register = async (params: RegisterParams, extras: any) => {
    const data = {
        code: params.code,
        firstName: params.firstName,
        lastName: params.lastName,
        email: params.email,
        password: params.password,
        username: params.username,
        firebaseUid: params.firebaseUid,
        ...extras,
    };
    return apiPost('/register', data, {}, '');
};

/**
 * Send a forgot password email using the backend
 * @param email The user's email
 * @param username The user's username
 * @returns Promise with result
 */
export const forgotPassword = async (email: string, username: string) => {
    return apiPost('/auth/forgot-password', { email, username }, {}, '');
};

/**
 * Send an email verification link using the backend
 * @param email The user's email
 * @returns Promise with result
 */
export const sendVerificationEmail = async (email: string) => {
    return apiPost('/auth/send-verification', { email }, {}, '');
};

/**
 * Reset a user's password
 * @param params Password reset parameters
 * @returns Promise with password reset result
 */
export const resetPassword = async (params: ResetPasswordParams) => {
    return apiPost(
        `/user/update/password`,
        params,
        {},
        '',
        true,
    );
};

/**
 * Validate an email address
 * @param email The email to validate
 * @returns Promise with validation result
 */
export const validateEmail = async (email: string) => {
    return apiGet(`/email?email=${encodeURIComponent(email)}`, {}, '');
};

/**
 * Get a user by username
 * @param username The username to look up
 * @returns Promise with user data
 */
export const getUser = async (username: string) => {
    return apiGet(`/user/${username}`, {}, '');
};

/**
 * Send a recovery code to the user's email
 */
export const apiSendRecoveryCode = async (params: SendRecoveryCodeParams) => {
    return apiPost('/user/send-code', params, {}, '');
};

/**
 * Report a user
 * @param params Report parameters
 * @returns Promise with report result
 */
export const reportUser = async (params: ReportUserParams) => {
    return apiGet(
        `${reportUserURL}?id=${encodeURIComponent(
            params.id,
        )}&idea=${encodeURIComponent(params.idea)}&reportedBy=${encodeURIComponent(
            params.reportedBy,
        )}&dateReported=${encodeURIComponent(params.dateReported)}`,
        {},
        '',
    );
};

/**
 * Get a user by ID
 * @param id The user ID
 * @returns Promise with user data
 */
export const getUserById = async (id: string) => {
    return apiGet(`/user/id/${id}`, {}, '');
};

/**
 * Update a user
 * @param params User data to update
 * @returns Promise with update result
 */
export const updateUser = async (params: UpdateUserParams) => {
    return apiPost('/user/update', params, {}, 'authenticated', true);
};

/**
 * Update a user's password
 * @param params Password update parameters
 * @returns Promise with update result
 */
export const updatePassword = async (params: UpdatePasswordParams) => {
    return apiPost('/user/update/password', params, {}, '');
};

/**
 * Deactivate a user account
 * @param params Deactivation parameters
 * @returns Promise with deactivation result
 */
export const deactivate = async (params: DeactivateParams) => {
    // 'authenticated' so an expired token gets refreshed-and-retried rather
    // than failing outright — this endpoint now requires auth server-side.
    return apiPost('/user/deactivate', params, {}, 'authenticated');
};